import { Descriptor, ObjectDescriptor, UiSchemaDescriptor } from '../../../models/Scorecard'
import {
  FormFieldFormTemplateMappingSource,
  FormTemplateMappingSource,
  FormTemplatePreview,
  VariableFormTemplateMappingSource,
  VariableFormTemplateMappingSourceId,
} from '../../../models/FormTemplate'

import { FORM_UI_WIDGET_TYPE_TO_FORM_FIELD_TYPE_MAPPING } from '../AIForms/model'

export const FORM_TEMPLATE_VARIABLE_SOURCE_ID_TO_LABEL_MAPPING: Record<VariableFormTemplateMappingSourceId, string> = {
  [VariableFormTemplateMappingSourceId.USER_NAME]: 'User Name',
  [VariableFormTemplateMappingSourceId.SESSION_STARTED_AT]: 'Session Started At',
  [VariableFormTemplateMappingSourceId.SESSION_FINISHED_AT]: 'Session Finished At',
  [VariableFormTemplateMappingSourceId.CURRENT_DATE]: 'Current Date',
  [VariableFormTemplateMappingSourceId.CURRENT_DATE_TIME]: 'Current Date And Time',
}

export const getFormFieldFormTemplateMappingSourcesFromDescriptorEntry = (key: string, descriptor: Descriptor): FormFieldFormTemplateMappingSource[] => {
  const source: FormFieldFormTemplateMappingSource = { type: 'form_field', path: `$.${key}` }

  const options = getOptionsForDescriptor(descriptor)
  if (!options?.length) {
    return [source]
  }

  return options.map((option) => ({ ...source, option }))
}

const getVariableFormTemplateMappingSourceFromVariableId = (variableId: VariableFormTemplateMappingSourceId): VariableFormTemplateMappingSource => ({
  type: 'variable',
  id: variableId,
})

export const getContextForFormTemplateMappingSuggestion = (formTemplatePreview: FormTemplatePreview, descriptor: ObjectDescriptor) => {
  const formFieldSources = Object.entries(descriptor.properties).reduce<FormFieldFormTemplateMappingSource[]>(
    (acc, [key, descriptor]) => [...acc, ...getFormFieldFormTemplateMappingSourcesFromDescriptorEntry(key, descriptor)],
    [],
  )
  const variableSources = Object.values(VariableFormTemplateMappingSourceId).map(getVariableFormTemplateMappingSourceFromVariableId)
  return { targets: formTemplatePreview.targets, sources: [...formFieldSources, ...variableSources] }
}

export const sortDescriptors = (descriptorA: Descriptor, descriptorB: Descriptor) => {
  const { index: indexA } = descriptorA
  const { index: indexB } = descriptorB
  if (indexA === undefined) {
    return 1
  }
  if (indexB === undefined) {
    return -1
  }
  return indexA - indexB
}

export const doSourcesMatch = (sourceA: FormTemplateMappingSource, sourceB: FormTemplateMappingSource): boolean => {
  if (sourceA.type !== sourceB.type) {
    return false
  }

  if (sourceA.type === 'form_field' && sourceB.type === 'form_field') {
    const doOptionsMatch = (sourceA.option && sourceB.option && sourceA.option === sourceB.option) || (!sourceA.option && !sourceB.option)

    return sourceA.path === sourceB.path && doOptionsMatch
  }

  if (sourceA.type === 'variable' && sourceB.type === 'variable') {
    return sourceA.id === sourceB.id
  }

  return false
}

export const getOptionsForDescriptor = (descriptor: Descriptor) =>
  descriptor.type === 'string' && descriptor.enum?.length
    ? descriptor.enum
    : descriptor.type === 'array' && descriptor.items.type === 'string' && descriptor.items.enum?.length
      ? descriptor.items.enum
      : null

export const mappingExistsForSource = (source: FormTemplateMappingSource, mapping: Record<string, FormTemplateMappingSource>) =>
  !!Object.values(mapping).find((mappingSource) => doSourcesMatch(source, mappingSource))

export const countFormFieldSourcesInDescriptor = (descriptor: ObjectDescriptor) =>
  Object.values(descriptor.properties).reduce<number>((acc, descriptor) => {
    const options = getOptionsForDescriptor(descriptor)
    if (options) {
      return acc + options.length
    }
    return acc + 1
  }, 0)

export const countMappedFormFieldSources = (mapping: Record<string, FormTemplateMappingSource>) =>
  Object.values(mapping).reduce<FormFieldFormTemplateMappingSource[]>((acc, source) => {
    if (source.type !== 'form_field') {
      return acc
    }

    const sourceAlreadyCounted = acc.some((accSource) => doSourcesMatch(source, accSource))
    if (sourceAlreadyCounted) {
      return acc
    }

    return [...acc, source]
  }, []).length

export const isResultValidMappingSuggestion = (result: unknown): result is Record<string, FormTemplateMappingSource> => {
  return typeof result === 'object' && result !== null && !Array.isArray(result)
}

export const getFormFieldTypeFromDescriptor = (descriptor: Descriptor, uiSchemaDescriptor?: UiSchemaDescriptor): string => {
  const uiWidget = uiSchemaDescriptor?.['ui:widget']

  if (!uiWidget) {
    return descriptor.type
  }

  const formFieldType = FORM_UI_WIDGET_TYPE_TO_FORM_FIELD_TYPE_MAPPING[uiWidget]
  if (!formFieldType) {
    return descriptor.type
  }

  return formFieldType
}
